/**
 * Content Service
 *
 * API calls for FAQ, Testimonial, Banner, and Contact management
 */

import { api } from '../lib/api';

export type ContentStatus = 'ACTIVE' | 'INACTIVE';
export type ContactStatus = 'NEW' | 'READ' | 'RESPONDED' | 'RESOLVED';

// FAQ Types
export interface FAQ {
  id: string;
  category: string;
  question: string;
  answer: string;
  displayOrder: number;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFAQInput {
  category: string;
  question: string;
  answer: string;
  displayOrder?: number;
  status?: ContentStatus;
}

// Testimonial Types
export interface Testimonial {
  id: string;
  customerName: string;
  company: string | null;
  photoUrl: string | null;
  text: string;
  rating: number;
  displayOrder: number;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTestimonialInput {
  customerName: string;
  company?: string;
  photoUrl?: string;
  text: string;
  rating: number;
  displayOrder?: number;
  status?: ContentStatus;
}

// Banner Types
export interface Banner {
  id: string;
  title: string;
  imageUrl: string;
  linkUrl: string | null;
  displayOrder: number;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBannerInput {
  title: string;
  imageUrl: string;
  linkUrl?: string;
  displayOrder?: number;
  status?: ContentStatus;
}

// Contact Types
export interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string;
  message: string;
  status: ContactStatus;
  createdAt: string;
  updatedAt: string;
}

// Catalogue Types
export interface CatalogueItem {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  bannerUrl: string | null;
  productCount: number;
  catalogueUrl: string | null;
  catalogueGeneratedAt: string | null;
  downloadCount: number;
  status: 'generated' | 'pending' | 'outdated';
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

// FAQ Service
export const faqService = {
  list: async (options: { page?: number; limit?: number; category?: string; status?: ContentStatus } = {}): Promise<PaginatedResponse<FAQ>> => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.category) params.append('category', options.category);
    if (options.status) params.append('status', options.status);

    const response = await api.get<FAQ[]>(`/admin/faqs?${params.toString()}`);
    return {
      data: response.data || [],
      meta: response.meta || { page: 1, limit: 50, total: 0, hasMore: false },
    };
  },

  getById: async (id: string): Promise<FAQ> => {
    const response = await api.get<FAQ>(`/admin/faqs/${id}`);
    return response.data!;
  },

  create: async (data: CreateFAQInput): Promise<FAQ> => {
    const response = await api.post<FAQ>('/admin/faqs', data);
    return response.data!;
  },

  update: async (id: string, data: Partial<CreateFAQInput>): Promise<FAQ> => {
    const response = await api.patch<FAQ>(`/admin/faqs/${id}`, data);
    return response.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/faqs/${id}`);
  },

  reorder: async (orders: Array<{ id: string; displayOrder: number }>): Promise<void> => {
    await api.post('/admin/faqs/reorder', { orders });
  },

  getCategories: async (): Promise<Array<{ category: string; count: number }>> => {
    const response = await api.get<Array<{ category: string; count: number }>>('/admin/faqs/categories');
    return response.data || [];
  },
};

// Testimonial Service
export const testimonialService = {
  list: async (options: { page?: number; limit?: number; status?: ContentStatus } = {}): Promise<PaginatedResponse<Testimonial>> => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.status) params.append('status', options.status);

    const response = await api.get<Testimonial[]>(`/admin/testimonials?${params.toString()}`);
    return {
      data: response.data || [],
      meta: response.meta || { page: 1, limit: 50, total: 0, hasMore: false },
    };
  },

  getById: async (id: string): Promise<Testimonial> => {
    const response = await api.get<Testimonial>(`/admin/testimonials/${id}`);
    return response.data!;
  },

  create: async (data: CreateTestimonialInput): Promise<Testimonial> => {
    const response = await api.post<Testimonial>('/admin/testimonials', data);
    return response.data!;
  },

  update: async (id: string, data: Partial<CreateTestimonialInput>): Promise<Testimonial> => {
    const response = await api.patch<Testimonial>(`/admin/testimonials/${id}`, data);
    return response.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/testimonials/${id}`);
  },
};

// Banner Service
export const bannerService = {
  list: async (options: { page?: number; limit?: number; status?: ContentStatus } = {}): Promise<PaginatedResponse<Banner>> => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.status) params.append('status', options.status);

    const response = await api.get<Banner[]>(`/admin/banners?${params.toString()}`);
    return {
      data: response.data || [],
      meta: response.meta || { page: 1, limit: 50, total: 0, hasMore: false },
    };
  },

  getById: async (id: string): Promise<Banner> => {
    const response = await api.get<Banner>(`/admin/banners/${id}`);
    return response.data!;
  },

  create: async (data: CreateBannerInput): Promise<Banner> => {
    const response = await api.post<Banner>('/admin/banners', data);
    return response.data!;
  },

  update: async (id: string, data: Partial<CreateBannerInput>): Promise<Banner> => {
    const response = await api.patch<Banner>(`/admin/banners/${id}`, data);
    return response.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/banners/${id}`);
  },

  reorder: async (orders: Array<{ id: string; displayOrder: number }>): Promise<void> => {
    await api.post('/admin/banners/reorder', { orders });
  },
};

// Contact Service
export const contactService = {
  list: async (options: { page?: number; limit?: number; status?: ContactStatus } = {}): Promise<PaginatedResponse<Contact>> => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.status) params.append('status', options.status);

    const response = await api.get<Contact[]>(`/admin/contacts?${params.toString()}`);
    return {
      data: response.data || [],
      meta: response.meta || { page: 1, limit: 20, total: 0, hasMore: false },
    };
  },

  getById: async (id: string): Promise<Contact> => {
    const response = await api.get<Contact>(`/admin/contacts/${id}`);
    return response.data!;
  },

  updateStatus: async (id: string, status: ContactStatus): Promise<Contact> => {
    const response = await api.patch<Contact>(`/admin/contacts/${id}/status`, { status });
    return response.data!;
  },

  reply: async (id: string, message: string): Promise<Contact> => {
    const response = await api.post<Contact>(`/admin/contacts/${id}/reply`, { message });
    return response.data!;
  },
};

// Catalogue Service
export const catalogueService = {
  list: async (): Promise<CatalogueItem[]> => {
    const response = await api.get<CatalogueItem[]>('/admin/catalogues');
    return response.data || [];
  },

  regenerate: async (categoryId: string): Promise<{ message: string; jobId: string }> => {
    const response = await api.post<{ message: string; jobId: string }>(`/admin/catalogues/${categoryId}/regenerate`);
    return response.data!;
  },

  regenerateAll: async (): Promise<{ message: string; count: number }> => {
    const response = await api.post<{ message: string; count: number }>('/admin/catalogues/regenerate-all');
    return response.data!;
  },
};
